/**
 * Order Lookup Action
 * Triggered by: arc:action:orderLookup
 */

import shopifyHelper from "@js/utils/shopify";
import GET_ORDER_INFO from '@js/graphql/2024_01/order/get.graphql';
import { hasher } from "@js/stores/hasher";

// Global state for order information
let currentOrder = null;
const useAuthenticated = true;

export const runOrderLookup = async (data = {}) => {
    try {
        const { 
            orderId, 
            orderNumber,
            useAuth = useAuthenticated 
        } = data;

        if (!orderId && !orderNumber) {
            throw new Error('Either orderId or orderNumber is required');
        }

        // Convert order number to ID if needed
        let finalOrderId = orderId;
        if (!finalOrderId && orderNumber) {
            // Remove # if present and use the number
            finalOrderId = orderNumber.replace('#', '');
        }

        if (window.enableDebugMode) { console.log('Retrieving Order Info for:', finalOrderId); }

        const orderInfo = await getOrderTrackingInfo(finalOrderId, useAuth);

        if (orderInfo) {
            // Dispatch success event
            window.dispatchEvent(new CustomEvent('order:found', {
                detail: {
                    order: currentOrder,
                    orderId: finalOrderId,
                    rawResponse: orderInfo
                }
            }));

            return {
                success: true,
                order: currentOrder,
                orderId: finalOrderId,
                message: 'Order found successfully'
            };
        } else {
            // Dispatch not found event
            window.dispatchEvent(new CustomEvent('order:notFound', {
                detail: {
                    orderId: finalOrderId,
                    searchedBy: orderId ? 'id' : 'number'
                }
            }));

            return {
                success: false,
                order: null,
                orderId: finalOrderId,
                message: 'Order not found'
            };
        }

    } catch (error) {
        console.error('Order Lookup error:', error);
        
        window.dispatchEvent(new CustomEvent('order:error', {
            detail: {
                error: error.message,
                action: 'lookup'
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Helper function to get order tracking information
const getOrderTrackingInfo = async (orderId, useAuth = true) => {
    try {
        const body = JSON.stringify({
            query: GET_ORDER_INFO.loc.source.body,
            variables: {
                id: `gid://shopify/Order/${orderId}`
            }
        });

        let data = {
            options: {
                method: 'POST',
                headers: {
                    'X-Shopify-Access-Token': '<SHOPIFY_API_AUTHENTICATED_KEY>',
                    'Content-Type': 'application/json',
                    'X-MindArc-Hash': hasher.store().getHash()
                },
                body: body,
                request_body: GET_ORDER_INFO.loc.source.body,
            }
        };

        let jsonResponse = null;
        if (useAuth) {
            jsonResponse = await shopifyHelper.authenticatedFetch(data);
        } else {
            jsonResponse = await shopifyHelper.unauthenticatedFetch(body);
        }

        if (jsonResponse.data?.order) {
            let shippingLineType = null;
            let trackingInfo = null;

            // Extract shipping line information
            jsonResponse.data.order.shippingLines.edges.forEach(node => {
                shippingLineType = node.node.title;
            });

            // Extract tracking information
            jsonResponse.data.order.fulfillments.forEach(fulfillment => {
                if (fulfillment.trackingInfo && fulfillment.trackingInfo.length > 0) {
                    trackingInfo = {
                        company: fulfillment.trackingInfo[0].company,
                        url: fulfillment.trackingInfo[0].url,
                        number: fulfillment.trackingInfo[0].number,
                    };
                }
            });

            // Store processed order information
            currentOrder = {
                id: orderId,
                name: jsonResponse.data.order.name,
                delivery_type: shippingLineType,
                tracking: trackingInfo,
                status: jsonResponse.data.order.displayFulfillmentStatus,
                financialStatus: jsonResponse.data.order.displayFinancialStatus,
                createdAt: jsonResponse.data.order.createdAt,
                totalPrice: jsonResponse.data.order.totalPriceSet?.shopMoney?.amount,
                currency: jsonResponse.data.order.totalPriceSet?.shopMoney?.currencyCode,
                lineItems: jsonResponse.data.order.lineItems?.edges?.map(edge => edge.node) || []
            };

            // Dispatch order data updated event
            window.dispatchEvent(new CustomEvent('trackorder-search', {
                detail: {
                    order: currentOrder
                }
            }));

            return jsonResponse;
        } else {
            currentOrder = null;
            return null;
        }

    } catch (error) {
        console.error('Error fetching order tracking info:', error);
        currentOrder = null;
        throw error;
    }
};

// Get current order information
export const getCurrentOrder = () => {
    return currentOrder;
};

// Clear current order
export const clearCurrentOrder = () => {
    currentOrder = null;
    window.dispatchEvent(new CustomEvent('order:cleared', {
        detail: {
            timestamp: Date.now()
        }
    }));
};

// Subscribe to order lookup events (backward compatibility)
export const subscribeToOrderLookup = (callback) => {
    window.addEventListener('trackorder-search', callback);
    return () => {
        window.removeEventListener('trackorder-search', callback);
    };
};

// Initialize order lookup system
export const initializeOrderLookup = (config = {}) => {
    const {
        autoSetupForms = true,
        formSelector = '[data-order-lookup-form]',
        inputSelector = '[data-order-input]',
        submitSelector = '[data-order-submit]'
    } = config;

    if (autoSetupForms) {
        setupOrderLookupForms(formSelector, inputSelector, submitSelector);
    }

    // Dispatch initialization event
    window.dispatchEvent(new CustomEvent('orderLookup:initialized', {
        detail: {
            config,
            timestamp: Date.now()
        }
    }));
};

// Setup order lookup form event listeners
const setupOrderLookupForms = (formSelector, inputSelector, submitSelector) => {
    const forms = document.querySelectorAll(formSelector);
    
    forms.forEach(form => {
        const input = form.querySelector(inputSelector);
        const submitBtn = form.querySelector(submitSelector);

        if (input && submitBtn) {
            // Remove existing listeners
            submitBtn.removeEventListener('click', handleOrderLookupSubmit);
            form.removeEventListener('submit', handleOrderLookupSubmit);

            // Add new listeners
            submitBtn.addEventListener('click', handleOrderLookupSubmit);
            form.addEventListener('submit', handleOrderLookupSubmit);
        }
    });
};

// Handle order lookup form submissions
const handleOrderLookupSubmit = (event) => {
    event.preventDefault();
    
    const form = event.target.closest('[data-order-lookup-form]');
    const input = form?.querySelector('[data-order-input]');
    const orderValue = input?.value?.trim();

    if (orderValue) {
        // Determine if it's an order number or ID
        const isOrderNumber = orderValue.includes('#') || isNaN(orderValue);
        
        window.dispatchEvent(new CustomEvent('arc:action:orderLookup', {
            detail: {
                [isOrderNumber ? 'orderNumber' : 'orderId']: orderValue
            }
        }));
    }
};

export default runOrderLookup;
